"use client"

import { COMPANY, CONTACT, LEGAL_LINKS, NAVIGATION, SOCIAL_LINKS } from '@/lib/constants'
import { cityData } from '@/lib/cities-data'
import { serviceDetails } from '@/lib/services-data'
import { motion } from "framer-motion"
import { Award, Mail, MapPin, Phone, Shield, Truck } from "lucide-react"
import Link from "next/link"

export function Footer() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  }

  const topCities = cityData.slice(0, 8)
  const topServices = serviceDetails.slice(0, 6)

  return (
    <motion.footer
      className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
      </div>

      {/* Trust Badges Bar */}
      <div className="relative border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <motion.div variants={itemVariants} className="flex flex-col items-center">
              <Award className="w-8 h-8 text-orange-400 mb-2" />
              <div className="text-lg font-bold">ISO Certified</div>
              <div className="text-xs text-white/60">Quality Assured</div>
            </motion.div>
            <motion.div variants={itemVariants} className="flex flex-col items-center">
              <Truck className="w-8 h-8 text-blue-400 mb-2" />
              <div className="text-lg font-bold">1000+ Moves</div>
              <div className="text-xs text-white/60">Successfully Completed</div>
            </motion.div>
            <motion.div variants={itemVariants} className="flex flex-col items-center">
              <Shield className="w-8 h-8 text-green-400 mb-2" />
              <div className="text-lg font-bold">100% Insured</div>
              <div className="text-xs text-white/60">Full Protection</div>
            </motion.div>
            <motion.div variants={itemVariants} className="flex flex-col items-center">
              <Phone className="w-8 h-8 text-purple-400 mb-2" />
              <div className="text-lg font-bold">24/7 Support</div>
              <div className="text-xs text-white/60">Always Available</div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          
          {/* Company Info - Larger Section */}
          <motion.div className="lg:col-span-4 space-y-6" variants={itemVariants}>
            <div>
              <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-blue-400 to-orange-400 bg-clip-text text-transparent">
                {COMPANY.name}
              </h3>
              <p className="text-white/70 leading-relaxed mb-4">
                {COMPANY.tagline}
              </p>
              <p className="text-sm text-white/60 leading-relaxed">
                India's most trusted moving partner since {COMPANY.established}. We transform relocations into seamless experiences with professional care and dedication.
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href={`tel:${CONTACT.phone.primary}`}
                className="flex items-start group hover:text-orange-400 transition-colors"
              >
                <Phone className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-orange-400" />
                <div>
                  <div className="text-sm text-white/60">Call Us 24/7</div>
                  <div className="font-semibold">{CONTACT.phone.display}</div>
                </div>
              </a>
              
              <a 
                href={`mailto:${CONTACT.email.primary}`}
                className="flex items-start group hover:text-orange-400 transition-colors"
              >
                <Mail className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-blue-400" />
                <div>
                  <div className="text-sm text-white/60">Email Us</div>
                  <div className="font-semibold break-all">{CONTACT.email.primary}</div>
                </div>
              </a>
              
              <div className="flex items-start">
                <MapPin className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-green-400" />
                <div>
                  <div className="text-sm text-white/60">Head Office</div>
                  <div className="text-sm">{CONTACT.address.full}</div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <div className="text-sm font-semibold mb-3 text-white/80">Connect With Us</div>
              <div className="flex gap-3">
                {SOCIAL_LINKS.map((social) => {
                  const Icon = social.icon
                  return (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-10 h-10 rounded-full bg-gradient-to-br ${social.color} flex items-center justify-center hover:shadow-lg transition-shadow`}
                      aria-label={`Visit our ${social.name} page`}
                    >
                      <Icon size={18} className="text-white" />
                    </motion.a>
                  )
                })}
              </div>
            </div>
          </motion.div>

          {/* Services */}
          <motion.div className="lg:col-span-2 space-y-4" variants={itemVariants}>
            <h4 className="font-bold text-lg mb-4 text-white">Our Services</h4>
            <ul className="space-y-2.5">
              {topServices.map((service) => (
                <li key={service.id}>
                  <Link 
                    href={`/services/${service.slug}`} 
                    className="text-sm text-white/70 hover:text-orange-400 hover:translate-x-1 inline-block transition-all"
                  >
                    → {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Cities We Serve */}
          <motion.div className="lg:col-span-3 space-y-4" variants={itemVariants}>
            <h4 className="font-bold text-lg mb-4 text-white">Cities We Serve</h4>
            <ul className="grid grid-cols-2 gap-2.5">
              {topCities.map((city) => (
                <li key={city.id}>
                  <Link 
                    href={`/locations/${city.slug}`} 
                    className="text-sm text-white/70 hover:text-blue-400 hover:translate-x-1 inline-block transition-all"
                  >
                    → {city.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link 
              href="/locations" 
              className="inline-block text-sm font-semibold text-orange-400 hover:text-orange-300 mt-2"
            >
              View All Cities →
            </Link>
          </motion.div>

          {/* Quick Links */}
          <motion.div className="lg:col-span-3 space-y-4" variants={itemVariants}>
            <h4 className="font-bold text-lg mb-4 text-white">Quick Links</h4>
            <ul className="space-y-2.5">
              <li>
                <Link href="/about" className="text-sm text-white/70 hover:text-orange-400 hover:translate-x-1 inline-block transition-all">
                  → About Us
                </Link>
              </li>
              {NAVIGATION.map((item) => (
                <li key={item.name}>
                  <Link 
                    href={item.href} 
                    className="text-sm text-white/70 hover:text-orange-400 hover:translate-x-1 inline-block transition-all"
                  >
                    → {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* SEO-Friendly Description */}
        <motion.div 
          className="mb-12 p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
          variants={itemVariants}
        >
          <p className="text-sm text-white/60 leading-relaxed text-center">
            <strong className="text-white">Bigwin Relocation</strong> is India's leading packers and movers company, offering professional relocation services across major cities including Bhubaneswar, Bangalore, Mumbai, Delhi, Pune, Hyderabad, Chennai, and Kolkata. 
            We specialize in household shifting, office relocation, car transportation, and international moving services with ISO certification, complete insurance coverage, and 24/7 customer support. 
            Trust our experienced team for safe, reliable, and affordable moving solutions.
          </p>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div 
          className="border-t border-white/10 pt-8"
          variants={itemVariants}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-sm text-white/60 text-center md:text-left">
              <p>&copy; {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
              <p className="text-xs mt-1">Trusted Packers and Movers Since {COMPANY.established}</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {LEGAL_LINKS.map((link) => (
                <Link 
                  key={link.name} 
                  href={link.href} 
                  className="text-white/60 hover:text-orange-400 transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.footer>
  )
}
